import { Center, Container, HStack, Heading, IconButton, Pagination, SkeletonText, Table } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { z } from 'zod';

import { CompaniesService } from '@/client/index.ts';
import ActionsMenu from '@/components/common/ActionsMenu.tsx';
import Navbar from '@/components/common/Navbar.tsx';
import AddUser from '@/components/users/AddUser';
import FilterUsers from '@/components/users/FilterUsers';

// Validate and provide defaults for search params
const usersSearchSchema = z.object({
  page: z.number().catch(1),
  filters: z.string().catch(''),
  sort: z.string().catch(''),
});

export const Route = createFileRoute('/_layout/$slug/access-management')({
  component: AccessManagement,
  validateSearch: (search) => usersSearchSchema.parse(search),
});

const PER_PAGE = 10;

function getUsersQueryOptions({
  slug,
  page,
  filters,
  sort,
}: {
  slug: string;
  page: number;
  filters: string;
  sort: string;
}) {
  return {
    queryKey: ['users', { page, filters, sort }],
    queryFn: async () => {
      return await CompaniesService.readCompanyUsers({
        offset: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        companySlug: slug,
        filters: filters,
        sort: sort,
      });
    },
  };
}

function UsersTable() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();

  const { page, filters, sort } = Route.useSearch();
  const { slug } = useParams({ strict: false });

  const parsedSorting = useMemo(() => {
    return sort ? [{ id: sort.split(':')[0], desc: sort.split(':')[1] === 'desc' }] : [];
  }, [sort]);

  const { data: usersData, isLoading } = useQuery({
    ...getUsersQueryOptions({ slug, page, filters, sort }),
  });

  const columns = [
    {
      accessorKey: 'email',
      header: t('user.columnsHeader.email'),
    },
    {
      accessorKey: 'first_name',
      header: t('user.columnsHeader.firstName'),
    },
    {
      accessorKey: 'last_name',
      header: t('user.columnsHeader.lastName'),
    },
    {
      id: 'actions', // Non-data column needs an id
      header: '',
      cell: ({ row }: { row: any }) => (
        <Center>
          <ActionsMenu type={'User'} value={row.original} />
        </Center>
      ),
      maxWidth: 50,
    },
  ];

  const table = useReactTable({
    data: usersData?.data || [],
    columns,
    state: {
      globalFilter: filters,
      sorting: parsedSorting,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(parsedSorting) : updater;
      const newSort = newSorting.length ? `${newSorting[0].id}:${newSorting[0].desc ? 'desc' : 'asc'}` : '';
      navigate({
        search: (prev: Record<string, string>) => ({ ...prev, sort: newSort }),
      });
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    if (!usersData?.count) return;
    if (usersData.count < page * PER_PAGE && page > 1) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          page: Math.floor(usersData.count / PER_PAGE) + 1,
        }),
      });
    }
  });

  return (
    <>
      <FilterUsers />
      <Table.Root size={{ base: 'sm', md: 'md' }} striped>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Header key={headerGroup.id}>
            <Table.Row>
              {headerGroup.headers.map((header) => (
                <Table.ColumnHeader key={header.id} onClick={header.column.getToggleSortingHandler()}>
                  <Center inline gap="2">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() ? (
                      header.column.getIsSorted() === 'desc' ? (
                        <FaCaretDown aria-label="sorted descending" />
                      ) : (
                        <FaCaretUp aria-label="sorted ascending" />
                      )
                    ) : null}
                  </Center>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
        ))}
        {isLoading ? (
          <Table.Body>
            {new Array(PER_PAGE).fill(null).map((_, i) => (
              <Table.Row key={i}>
                {new Array(9).fill(null).map((_, j) => (
                  <Table.Cell key={j}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        ) : (
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table.Root>
      <Pagination.Root
        count={usersData?.count || 0}
        pageSize={PER_PAGE}
        page={page}
        onPageChange={(pageDetails) =>
          navigate({
            search: (prev: Record<string, string>) => ({
              ...prev,
              page: pageDetails.page,
            }),
          })
        }
      >
        <Center>
          <HStack wrap="wrap" mt={4}>
            <Pagination.PrevTrigger />
            <Pagination.Items
              render={(page) => <IconButton variant={{ base: 'ghost', _selected: 'outline' }}>{page.value}</IconButton>}
            />
            <Pagination.NextTrigger />
            <Pagination.NextTrigger />
          </HStack>
        </Center>
      </Pagination.Root>
    </>
  );
}

function AccessManagement() {
  const { t } = useTranslation();
  return (
    <Container maxW="full">
      <Heading size="lg" textAlign={{ base: 'center', md: 'left' }} pt={12}>
        {t('user.header')}
      </Heading>
      <Navbar type={'User'} addModalAs={AddUser} />
      <UsersTable />
    </Container>
  );
}
